import { useEffect, useState } from 'react';

import { useApiClient } from '../../providers/auth-provider';
import { InfoHint } from '../../shell/InfoHint';

import { AudienceBadge } from './AudienceBadge';
import { ScopePills } from './ScopePills';

import type {
  ApiKeyResponse,
  UpdateApiKeyBody,
} from '../../hooks/use-api-keys';
import type { ApiKeyCompletionsOptions, ApiKeyMetadata, MySkill } from '../../lib/api-client';

interface EditKeyModalProps {
  apiKey: ApiKeyResponse;
  onClose: () => void;
  /** From useApiKeys() — PATCHes the key and swaps the row in list state. */
  updateKey: (id: string, body: UpdateApiKeyBody) => Promise<ApiKeyResponse>;
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** ISO timestamp → value for an <input type="datetime-local"> (local time, minute precision). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((x) => set.has(x));
}

/** Short label for what a key acts as. */
function actsAsText(k: ApiKeyResponse): string {
  if (k.acts_as === 'agent') return `Agent (${k.agent_id ?? '—'})`;
  if (k.acts_as === 'role') return `Role only (${k.role_id ?? '—'})`;
  return 'Me (your user)';
}

export function EditKeyModal({ apiKey, onClose, updateKey }: EditKeyModalProps) {
  const apiClient = useApiClient();

  // --- Original (server) values, normalized for dirty-comparison ---
  const origName = apiKey.name ?? '';
  const origExpiryLocal = isoToLocalInput(apiKey.expires_at);
  const origCompletions = (apiKey.metadata?.completions ?? {}) as ApiKeyCompletionsOptions;
  const origVapiMode = !!origCompletions.vapi_mode;
  const origAssistantId = origCompletions.vapi_assistant_id ?? '';
  const origSessionMapping = !!origCompletions.vapi_session_mapping;
  const origFillerEnabled = origCompletions.filler_enabled !== false;
  const origNarrowed = apiKey.skill_ids !== null;
  const origSkillIds = apiKey.skill_ids ?? [];

  // --- Editable form state ---
  const [name, setName] = useState(origName);
  const [scopes, setScopes] = useState<string[]>([...apiKey.scopes]);
  const [expiresAt, setExpiresAt] = useState(origExpiryLocal);
  const [vapiMode, setVapiMode] = useState(origVapiMode);
  const [assistantId, setAssistantId] = useState(origAssistantId);
  const [vapiSessionMapping, setVapiSessionMapping] = useState(origSessionMapping);
  const [fillerEnabled, setFillerEnabled] = useState(origFillerEnabled);
  const [narrowSkills, setNarrowSkills] = useState(origNarrowed);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([...origSkillIds]);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [mySkills, setMySkills] = useState<MySkill[]>([]);

  const isOai = apiKey.audience === 'openai_completions';
  // Agent-bound keys use the agent's own skill grants — per-key narrowing doesn't apply.
  const skillsEditable = apiKey.acts_as !== 'agent';
  const busy = saveState === 'saving';

  // Lazy-load the creator's effective skills for the narrowing picker (same as MintKeyModal).
  useEffect(() => {
    if (!skillsEditable) return;
    apiClient.getMySkills().then(setMySkills).catch(() => {});
  }, [apiClient, skillsEditable]);

  function toggleScope(s: string) {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }
  function toggleSkill(id: string) {
    setSelectedSkillIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  // --- Dirty check: field-by-field vs the key row ---
  const nameChanged = name.trim() !== origName;
  const scopesChanged = !sameSet(scopes, apiKey.scopes);
  const expiryChanged = expiresAt !== origExpiryLocal;
  const completionsChanged =
    isOai &&
    (vapiMode !== origVapiMode ||
      (vapiMode &&
        (assistantId.trim() !== origAssistantId || vapiSessionMapping !== origSessionMapping)) ||
      fillerEnabled !== origFillerEnabled);
  const skillsChanged =
    skillsEditable &&
    (narrowSkills !== origNarrowed || (narrowSkills && !sameSet(selectedSkillIds, origSkillIds)));

  const dirty = nameChanged || scopesChanged || expiryChanged || completionsChanged || skillsChanged;

  async function save() {
    if (!dirty || busy) return;
    if (nameChanged && !name.trim()) {
      setError('Name is required');
      return;
    }
    if (completionsChanged && vapiMode && !assistantId.trim()) {
      setError('Assistant ID is required for VAPI voice keys');
      return;
    }
    setSaveState('saving');
    setError(null);
    try {
      // PATCH sends only the changed fields; explicit null clears expiry / skill narrowing.
      const body: UpdateApiKeyBody = {};
      if (nameChanged) body.name = name.trim();
      if (scopesChanged) body.scopes = scopes;
      if (expiryChanged) body.expires_at = expiresAt ? new Date(expiresAt).toISOString() : null;
      if (completionsChanged) {
        // Rebuild the completions bag the same way MintKeyModal does: VAPI fields only in
        // VAPI mode; filler_enabled only when opted out. metadata replaces the whole
        // object server-side, so carry over any non-completions keys unchanged.
        const completions: ApiKeyCompletionsOptions = {
          ...(vapiMode
            ? {
                vapi_mode: true,
                vapi_assistant_id: assistantId.trim(),
                ...(vapiSessionMapping ? { vapi_session_mapping: true } : {}),
              }
            : {}),
          ...(fillerEnabled ? {} : { filler_enabled: false }),
        };
        const { completions: _dropped, ...rest } = (apiKey.metadata ?? {}) as ApiKeyMetadata;
        body.metadata =
          Object.keys(completions).length > 0 ? { ...rest, completions } : { ...rest };
      }
      if (skillsChanged) body.skill_ids = narrowSkills ? selectedSkillIds : null;
      await updateKey(apiKey.id, body);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update key');
      setSaveState('error');
    }
  }

  const fieldLabel: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--text-soft)',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid var(--border)',
    borderRadius: 4,
    background: 'var(--bg-alt)',
    color: 'var(--text)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  return (
    <div
      data-testid="edit-key-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-labelledby="edit-key-modal-title"
        style={{
          width: 560,
          maxWidth: '92vw',
          maxHeight: '88vh',
          overflow: 'auto',
          background: 'var(--bg)',
          borderRadius: 8,
          padding: '20px 24px',
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div id="edit-key-modal-title" style={{ flex: 1, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
            Edit API key
          </div>
          <AudienceBadge audience={apiKey.audience} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 16 }}>
          Changes take effect on the key's next use — no re-issue needed.
        </div>

        {/* --- Immutable facts (read-only) --- */}
        <div
          data-testid="edit-key-immutable"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--bg-alt)',
            opacity: 0.8,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Meta label="Key" mono value={`${apiKey.key_prefix}…${apiKey.key_hint}`} />
            <Meta label="Acts as" value={actsAsText(apiKey)} />
            <Meta label="Created" value={fmt(apiKey.created_at)} />
            <Meta label="Last used" value={fmt(apiKey.last_used_at)} />
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-soft)' }}>
            Protocol, secret, and identity are baked into the key at mint time — create a new key to
            change them.
          </div>
        </div>

        {/* --- Name --- */}
        <label style={{ display: 'block', marginBottom: 16 }}>
          <div style={fieldLabel}>Name</div>
          <input
            data-testid="edit-key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            style={inputStyle}
          />
        </label>

        {/* --- Scopes --- */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...fieldLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
            Scopes
            <InfoHint label="What scopes do">
              Scopes are the operations this key may perform within its protocol. They{' '}
              <strong>narrow</strong> within the role, never widen it. Edits apply on the key's very
              next request.
            </InfoHint>
          </div>
          <ScopePills
            audience={apiKey.audience}
            selected={scopes}
            onToggle={toggleScope}
            disabled={busy}
          />
        </div>

        {/* --- Expiry --- */}
        <div style={{ marginBottom: 16 }}>
          <div style={fieldLabel}>Expires</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              data-testid="edit-key-expires"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={busy}
              style={{ ...inputStyle, width: 'auto', flex: 1 }}
            />
            {expiresAt ? (
              <button
                type="button"
                data-testid="edit-key-clear-expiry"
                onClick={() => setExpiresAt('')}
                disabled={busy}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: 'transparent',
                  color: 'var(--text-soft)',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-soft)' }}>No expiry</span>
            )}
          </div>
        </div>

        {/* --- OpenAI-completions sub-options --- */}
        {isOai && (
          <div style={{ marginBottom: 16 }}>
            <div style={fieldLabel}>OpenAI completions options</div>

            <label
              data-testid="edit-vapi-mode-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}
            >
              <input
                type="checkbox"
                checked={vapiMode}
                disabled={busy}
                onChange={(e) => setVapiMode(e.target.checked)}
              />
              This key is for VAPI voice
            </label>

            {vapiMode && (
              <div
                style={{
                  marginBottom: 10,
                  paddingLeft: 24,
                  borderLeft: '2px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>
                    Assistant ID <span style={{ color: 'var(--kind-failure, #c0392b)' }}>*</span>
                  </span>
                  <input
                    data-testid="edit-vapi-assistant-id"
                    value={assistantId}
                    onChange={(e) => setAssistantId(e.target.value)}
                    disabled={busy}
                    placeholder="e.g. 297af941-858f-45ee-8ce3-b209e16442e4"
                    style={inputStyle}
                  />
                </label>

                <label
                  data-testid="edit-vapi-session-mapping-toggle"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}
                >
                  <input
                    type="checkbox"
                    checked={vapiSessionMapping}
                    disabled={busy}
                    onChange={(e) => setVapiSessionMapping(e.target.checked)}
                  />
                  VAPI conversation → session mapping
                </label>
              </div>
            )}

            <label
              data-testid="edit-filler-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}
            >
              <input
                type="checkbox"
                checked={fillerEnabled}
                disabled={busy}
                onChange={(e) => setFillerEnabled(e.target.checked)}
              />
              Instant filler (reduce dead air)
            </label>
          </div>
        )}

        {/* --- Skill narrowing (self/role keys only) ---
            Also shown when the skill list is empty/unavailable but the key ALREADY
            carries narrowing — otherwise an existing restriction would be invisible
            and impossible to clear. */}
        {skillsEditable && (mySkills.length > 0 || (apiKey.skill_ids?.length ?? 0) > 0) && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
              <input
                type="checkbox"
                data-testid="edit-narrow-skills-toggle"
                checked={narrowSkills}
                disabled={busy}
                onChange={(e) => setNarrowSkills(e.target.checked)}
              />
              Restrict this key to specific skills
              <InfoHint label="About skill narrowing">
                Limit this key to a subset of the skills <strong>you</strong> can access — you can
                only ever narrow your own access. Unchecking removes the restriction.
              </InfoHint>
            </label>
            {narrowSkills && mySkills.length === 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-soft)' }}>
                This key is restricted to {apiKey.skill_ids?.length ?? 0} skill(s), but the skill list
                could not be loaded. Uncheck above to remove the restriction entirely.
              </div>
            )}
            {narrowSkills && (
              <div
                style={{
                  marginTop: 8,
                  maxHeight: 160,
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: 6,
                }}
              >
                {mySkills.map((sk) => {
                  const checked = selectedSkillIds.includes(sk.skill_id);
                  return (
                    <label
                      key={sk.skill_id}
                      data-testid={`edit-narrow-skill-${sk.skill_id}`}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 6px', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => toggleSkill(sk.skill_id)}
                      />
                      <span>
                        <span style={{ fontSize: 13, color: 'var(--text)' }}>{sk.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-soft)' }}>{sk.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {error && (
          <div
            data-testid="edit-key-error"
            role="alert"
            style={{
              marginBottom: 12,
              padding: '6px 10px',
              background: 'rgba(192,57,43,0.06)',
              border: '1px solid rgba(192,57,43,0.18)',
              borderRadius: 4,
              fontSize: 12,
              color: 'var(--kind-failure, #c0392b)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="edit-key-save"
            onClick={() => void save()}
            disabled={!dirty || busy}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: 0,
              borderRadius: 4,
              background: 'var(--accent)',
              color: '#fff',
              cursor: !dirty || busy ? 'not-allowed' : 'pointer',
              opacity: !dirty || busy ? 0.6 : 1,
              fontWeight: 500,
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text)',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}
